import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Tooltip,
  IconButton,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { useSnackBar } from '../../../../contexts/snackbar';
import { CheckCircle, ContentCopy, Launch } from '@mui/icons-material';
import Cookies from 'js-cookie';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';

export const SHOPIFY_SUBDOMAIN_INITIATOR_KEY = 'shopify_subdomain_initiator'

interface ShopifyIntegrationProps {
  connectedShop: string | null;
  refreshIntegrationData: ()=>Promise<void>;
}

function ShopifyIntegration({ connectedShop, refreshIntegrationData }: ShopifyIntegrationProps) {
  const { user, config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const [isConnecting, setIsConnecting] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const finialiseInstallationLock = useRef<boolean>(false);

  // ✅ Normalization helper
  const normalizeShopUrl = (input: string): string | null => {
    if (!input) return null;
    let clean = input.trim().toLowerCase();
    clean = clean.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const adminMatch = clean.match(/admin\.shopify\.com\/store\/([^/?#]+)/);
    if (adminMatch) return `${adminMatch[1]}.myshopify.com`;
    if (clean.endsWith('.myshopify.com')) return clean;
    return null;
  };

  // Detect Shopify redirect after OAuth
  useEffect(() => {
    const detectRedirectAfterOAuth = async () => {
      if (finialiseInstallationLock.current || connectedShop) return;

      const params = new URLSearchParams(window.location.search);
      const event = params.get('event');
      if (event === 'shopify_install') {
        const code = params.get('code');
        const shop = params.get('shop');
        const hmac = params.get('hmac');
        const timestamp = params.get('timestamp');

        if (shop && !connectedShop) {
          finialiseInstallationLock.current = true;
          setIsFinishing(true);
          try {
            if (!code) {
              await connectShopify(shop);
              return;
            }
            await axios.get(
              `${API_ENDPOINT}/shopify/v1/callback/?code=${code}&shop=${shop}&hmac=${hmac}&timestamp=${timestamp}`,
              config
            );
            refreshIntegrationData();
            showMessage('Shopify integration completed!');
          } catch (e) {
            console.log(e);
            showMessage('Shopify integration failed', 'error');
          } finally {
            setIsFinishing(false);
            const newUrl = `${window.location.pathname}?tab=1`;
            window.history.replaceState({}, document.title, newUrl);
          }
        }
      }
    };

    detectRedirectAfterOAuth();
  }, [connectedShop, config, showMessage]);

  const connectShopify = async (shopUrl: string) => {
    const normalized = normalizeShopUrl(shopUrl);

    if (!normalized) {
      showMessage('Invalid Shopify store URL. Please check and try again.', 'warning');
      return;
    }

    setIsConnecting(true);
    try {
      // 1️⃣ Capture current subdomain
      const subdomain = window.location.hostname.split('.')[0];

      // 2️⃣ Store it in a cookie accessible by all subdomains
      Cookies.set(SHOPIFY_SUBDOMAIN_INITIATOR_KEY, subdomain, {
        domain: '.parceltracer.app',
        path: '/',
        secure: true,
        sameSite: 'None',
        expires: 1 / 288, // 5 minutes
      });

      // 3️⃣ Proceed with Shopify install request
      const frontendUrl = window.location.origin;
      const response = await axios.get(
        `${API_ENDPOINT}/shopify/v1/install/?shop=${normalized}&frontend_url=${encodeURIComponent(frontendUrl)}`,
        config
      );

      // 4️⃣ Redirect user to Shopify App installation page
      window.location.href = response.data.install_url;
    } catch (error: any) {
      showMessage('Failed to start Shopify integration', 'error');
      console.log(error.response?.data || error);
    } finally {
      setIsConnecting(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const copyShopUrl = () => {
    if (!connectedShop) return;
    navigator.clipboard.writeText(connectedShop);
    showMessage('Shop URL copied to clipboard');
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {connectedShop ? (
        <>
          <Alert icon={<CheckCircle />} severity="success" sx={{ mb: 2, mt: 2 }}>
            Connected Shopify store
          </Alert>
          <TextField
            value={connectedShop}
            fullWidth
            InputProps={{
              readOnly: true,
              endAdornment: (
                <>
                  <Tooltip title="Copy shop URL">
                    <IconButton onClick={copyShopUrl}>
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Open Shopify store">
                    <IconButton onClick={() => window.open(`https://${connectedShop}`, '_blank')}>
                      <Launch />
                    </IconButton>
                  </Tooltip>
                </>
              ),
            }}
          />
        </>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Connect your Shopify store to enable automatic order sync
          </Alert>
          <Button
            variant="contained"
            onClick={() => {
              const subdomain = window.location.hostname.split('.')[0];
              // Store initiating subdomain in cookie before redirecting
              Cookies.set(SHOPIFY_SUBDOMAIN_INITIATOR_KEY, subdomain, {
                domain: '.parceltracer.app',
                path: '/',
                secure: true,
                sameSite: 'None',
                expires: 1 / 288, // 5 minutes
              });
              window.location.href = 'https://apps.shopify.com/parcel-tracer';
            }}
            disabled={isConnecting}
          >
            Connect to Shopify {isConnecting && <CircularProgress size={20} />}
          </Button>
        </>
      )}

      <CustomDialog open={isFinishing}>
        <DialogTitle>Finishing Shopify installation</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Please wait while we complete the setup...</Typography>
        </DialogContent>
      </CustomDialog>
    </Box>
  );
}

export default ShopifyIntegration;
